import { config } from '@grafana/runtime';
import { getDefaultAzureCloud } from '../clouds.js';
import { instanceOfAzureCredential } from './AzureCredentials.js';

const concealed = Symbol("Concealed client secret");
const concealedLegacy = Symbol("Concealed legacy client secret");
function isCredentialsComplete(credentials, ignoreSecret = false) {
  const authType = credentials.authType;
  switch (authType) {
    case "msi":
    case "workloadidentity":
    case "currentuser":
      return true;
    case "clientsecret":
    case "clientsecret-obo":
      return !!(credentials.azureCloud && credentials.tenantId && credentials.clientId && // When ignoreSecret is set we consider the credentials complete without checking the secret
      !!(ignoreSecret || credentials.clientSecret));
    case "ad-password":
      return !!(credentials.clientId && credentials.password && credentials.userId);
    default:
      throw new Error(`The auth type '${authType}' not supported.`);
  }
}
function getClientSecret(options) {
  if (!("secureJsonFields" in options) || !options.hasOwnProperty("secureJsonFields")) {
    return void 0;
  }
  if (options.secureJsonFields.azureClientSecret) {
    return concealed;
  } else if (options.secureJsonFields.clientSecret) {
    return concealedLegacy;
  } else {
    const secret = options.secureJsonData?.azureClientSecret;
    return typeof secret === "string" && secret.length > 0 ? secret : void 0;
  }
}
function getAdPassword(options) {
  if (!("secureJsonFields" in options) || !options.hasOwnProperty("secureJsonFields")) {
    return void 0;
  }
  if (options.secureJsonFields.password) {
    return concealed;
  } else {
    const secret = options.secureJsonData?.password;
    return typeof secret === "string" && secret.length > 0 ? secret : void 0;
  }
}
function getDatasourceCredentials(options, oboEnabled) {
  const credentials = options.jsonData.azureCredentials;
  if (!credentials) {
    return void 0;
  }
  const authType = credentials.authType;
  switch (authType) {
    case "msi":
    case "workloadidentity":
      if (authType === "msi" && config.azure.managedIdentityEnabled || authType === "workloadidentity" && config.azure.workloadIdentityEnabled) {
        return {
          authType
        };
      } else {
        return void 0;
      }
    case "clientsecret":
    case "clientsecret-obo":
      if (authType === "clientsecret-obo" && !oboEnabled) {
        return void 0;
      }
      return {
        authType,
        azureCloud: credentials.azureCloud || getDefaultAzureCloud(),
        tenantId: credentials.tenantId,
        clientId: credentials.clientId,
        clientSecret: getClientSecret(options)
      };
    case "ad-password":
      return {
        authType,
        userId: credentials.userId,
        clientId: credentials.clientId,
        password: getAdPassword(options)
      };
  }
  if (instanceOfAzureCredential(authType, credentials)) {
    if (!config.azure.userIdentityEnabled) {
      return void 0;
    }
    if (instanceOfAzureCredential("clientsecret", credentials.serviceCredentials)) {
      const serviceCredentials = { ...credentials.serviceCredentials, clientSecret: getClientSecret(options) };
      return {
        authType,
        serviceCredentialsEnabled: credentials.serviceCredentialsEnabled,
        serviceCredentials
      };
    }
    return {
      authType,
      serviceCredentialsEnabled: credentials.serviceCredentialsEnabled,
      serviceCredentials: credentials.serviceCredentials
    };
  }
  throw new Error(`The auth type '${authType}' is not supported.`);
}
function updateDatasourceCredentials(options, credentials, oboEnabled) {
  options = {
    ...options,
    jsonData: {
      ...options.jsonData,
      azureAuthType: void 0,
      cloudName: void 0,
      tenantId: void 0,
      clientId: void 0
    }
  };
  const authType = credentials.authType;
  switch (authType) {
    case "msi":
    case "workloadidentity":
      if (authType === "msi" && !config.azure.managedIdentityEnabled) {
        throw new Error("Managed Identity authentication is not enabled in Grafana config.");
      }
      if (authType === "workloadidentity" && !config.azure.workloadIdentityEnabled) {
        throw new Error("Workload Identity authentication is not enabled in Grafana config.");
      }
      options = {
        ...options,
        jsonData: {
          ...options.jsonData,
          azureCredentials: {
            authType
          }
        }
      };
      return options;
    case "clientsecret":
    case "clientsecret-obo":
      if (authType === "clientsecret-obo" && !oboEnabled) {
        throw new Error("Client Secret OBO authentication is not enabled in Grafana config.");
      }
      options = {
        ...options,
        jsonData: {
          ...options.jsonData,
          azureCredentials: {
            authType,
            azureCloud: credentials.azureCloud || getDefaultAzureCloud(),
            tenantId: credentials.tenantId,
            clientId: credentials.clientId
          }
        },
        secureJsonData: {
          ...options.secureJsonData,
          azureClientSecret: typeof credentials.clientSecret === "string" && credentials.clientSecret.length > 0 ? credentials.clientSecret : void 0
        },
        secureJsonFields: {
          ...options.secureJsonFields,
          azureClientSecret: credentials.clientSecret === concealed,
          clientSecret: credentials.clientSecret === concealedLegacy
        }
      };
      if (authType === "clientsecret-obo") {
        options = {
          ...options,
          jsonData: {
            ...options.jsonData,
            oauthPassThru: true
          }
        };
      }
      return options;
    case "ad-password":
      options = {
        ...options,
        jsonData: {
          ...options.jsonData,
          azureCredentials: {
            authType: "ad-password",
            userId: credentials.userId,
            clientId: credentials.clientId
          }
        },
        secureJsonData: {
          ...options.secureJsonData,
          password: typeof credentials.password === "string" && credentials.password.length > 0 ? credentials.password : void 0
        },
        secureJsonFields: {
          ...options.secureJsonFields,
          password: typeof credentials.password === "symbol"
        }
      };
      return options;
  }
  if (instanceOfAzureCredential("currentuser", credentials)) {
    if (!config.azure.userIdentityEnabled) {
      throw new Error("User Identity authentication is not enabled in Grafana config.");
    }
    const serviceCredentials = credentials.serviceCredentials;
    let clientSecret;
    if (instanceOfAzureCredential("clientsecret", serviceCredentials)) {
      clientSecret = serviceCredentials.clientSecret;
      delete serviceCredentials.clientSecret;
    }
    options = {
      ...options,
      jsonData: {
        ...options.jsonData,
        azureCredentials: {
          authType: "currentuser",
          serviceCredentialsEnabled: credentials.serviceCredentialsEnabled,
          serviceCredentials
        },
        oauthPassThru: true,
        disableGrafanaCache: true
      },
      secureJsonData: {
        ...options.secureJsonData,
        azureClientSecret: typeof clientSecret === "string" && clientSecret.length > 0 ? clientSecret : void 0
      },
      secureJsonFields: {
        ...options.secureJsonFields,
        azureClientSecret: clientSecret === concealed,
        clientSecret: clientSecret === concealedLegacy
      }
    };
    return options;
  }
  throw new Error(`The auth type '${authType}' is not supported.`);
}
function hasCredentials(options) {
  return !!options.jsonData.azureCredentials;
}

export { concealed, concealedLegacy, getAdPassword, getClientSecret, getDatasourceCredentials, hasCredentials, isCredentialsComplete, updateDatasourceCredentials };
//# sourceMappingURL=AzureCredentialsConfig.js.map
